class ShippingCalculator {
  // Used only when the theme strings have not been rendered, e.g. on a cached page
  static fallbackStrings = {
    progressLabel: 'Progress towards free shipping',
    empty: 'Add items to your basket for free shipping',
    threshold: 'Spend [amount] to get free shipping',
    remaining: 'Spend [amount] more to get free shipping',
    qualified: 'You qualify for free shipping!',
    unavailable: 'Shipping calculated at checkout',
  };

  constructor() {
    this.cache = new Map();
    this.listeners = new Set();
    this.currentShippingData = null;
    this.isCalculating = false;
    this.debounceTimer = null;

    // Progress bar state management
    this.progressBarState = {
      percentage: 0,
      message: this.getMessage('empty'),
    };

    // Debouncer for progress bar creation
    this.initializationDebouncer = null;

    this.defaultPostcodes = {
      GB: 'SW1A 1AA', // UK - Westminster, London
      US: '10001', // US - New York
      CA: 'K1A 0A6', // Canada - Ottawa
      AU: '2000', // Australia - Sydney
      DE: '10115', // Germany - Berlin
      FR: '75001', // France - Paris
      IT: '00118', // Italy - Rome
      ES: '28001', // Spain - Madrid
      NL: '1012', // Netherlands - Amsterdam
      BE: '1000', // Belgium - Brussels
      CH: '3001', // Switzerland - Bern
      AT: '1010', // Austria - Vienna
      SE: '11120', // Sweden - Stockholm
      NO: '0150', // Norway - Oslo
      DK: '1050', // Denmark - Copenhagen
      FI: '00100', // Finland - Helsinki
      IE: 'D01', // Ireland - Dublin
      PT: '1000-001', // Portugal - Lisbon
      PL: '00-001', // Poland - Warsaw
      CZ: '110 00', // Czech Republic - Prague
      HU: '1011', // Hungary - Budapest
      GR: '10431', // Greece - Athens
      JP: '100-0001', // Japan - Tokyo
      KR: '04524', // South Korea - Seoul
      SG: '018956', // Singapore
      HK: '999077', // Hong Kong
      NZ: '6011', // New Zealand - Wellington
      ZA: '8001', // South Africa - Cape Town
      BR: '01310-100', // Brazil - São Paulo
      MX: '06600', // Mexico - Mexico City
      AR: 'C1002', // Argentina - Buenos Aires
      CL: '8320000', // Chile - Santiago
      CO: '110111', // Colombia - Bogotá
      PE: '15001', // Peru - Lima
      IN: '110001', // India - New Delhi
      CN: '100000', // China - Beijing
      TH: '10100', // Thailand - Bangkok
      MY: '50000', // Malaysia - Kuala Lumpur
      PH: '1000', // Philippines - Manila
      ID: '10110', // Indonesia - Jakarta
      VN: '100000', // Vietnam - Hanoi
      TR: '34000', // Turkey - Istanbul
      RU: '101000', // Russia - Moscow
      UA: '01001', // Ukraine - Kyiv
      IL: '9100001', // Israel - Jerusalem
      AE: '00000', // UAE - Dubai
      SA: '11564', // Saudi Arabia - Riyadh
      EG: '11511', // Egypt - Cairo
      MA: '10000', // Morocco - Rabat
      NG: '100001', // Nigeria - Lagos
      KE: '00100', // Kenya - Nairobi
      GH: '00233', // Ghana - Accra
    };

    // Initialize progress bar management
    this.initProgressBarManagement();
  }

  // Initialize progress bar management system
  initProgressBarManagement() {
    // Set up DOM observer to watch for cart drawer container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if the cart drawer is added
            const cartDrawer = node.tagName === 'CART-DRAWER' ? node : node.querySelector?.('cart-drawer');
            if (cartDrawer) {
              this.debouncedEnsureProgressBar();
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Initialize progress bar immediately if cart drawer exists
    document.addEventListener('DOMContentLoaded', () => {
      this.ensureProgressBarExists();
    });

    // Also check immediately in case DOM is already loaded
    if (document.readyState === 'loading') {
      // DOM is still loading, event listener above will handle it
    } else {
      // DOM is already loaded
      this.ensureProgressBarExists();
    }
  }

  // Debounced progress bar creation to prevent rapid multiple calls
  debouncedEnsureProgressBar() {
    if (this.initializationDebouncer) {
      clearTimeout(this.initializationDebouncer);
    }

    this.initializationDebouncer = setTimeout(() => {
      this.ensureProgressBarExists();
    }, 50); // 50ms debounce
  }

  // Ensure progress bar exists and is properly positioned
  ensureProgressBarExists() {
    // Find the stable cart drawer container (doesn't get replaced)
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return; // Cart drawer not present
    }

    let progressBar = document.getElementById('shipping-progress-bar');

    // If progress bar doesn't exist, create it
    if (!progressBar) {
      progressBar = this.createProgressBarElement();

      // Find the drawer dialog container (stable, doesn't get replaced)
      const drawerDialog = cartDrawer.querySelector('#CartDrawer-Dialog');
      if (drawerDialog) {
        // Find the CartDrawer-Header element and insert the progress bar after it
        const cartHeader = drawerDialog.querySelector('#CartDrawer-Header');
        if (cartHeader && cartHeader.nextSibling) {
          drawerDialog.insertBefore(progressBar, cartHeader.nextSibling);
        } else if (cartHeader) {
          // If no next sibling, append after the header
          cartHeader.insertAdjacentElement('afterend', progressBar);
        } else {
          // Fallback: insert at the very top of the drawer dialog
          drawerDialog.insertBefore(progressBar, drawerDialog.firstChild);
        }
      } else {
        // Fallback: insert in cart drawer root
        cartDrawer.insertBefore(progressBar, cartDrawer.firstChild);
      }

      // Initialize with current state
      this.initializeProgressBarElement(progressBar);
    }
  }

  // Create the progress bar HTML element
  createProgressBarElement() {
    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'relative w-full px-4 pb-4 flex flex-col gap-2';
    progressBarContainer.id = 'shipping-progress-bar';

    const { percentage, message } = this.progressBarState;

    progressBarContainer.innerHTML = `
      <div
        class="relative w-full h-6 rounded-full bg-inactive/20 overflow-hidden"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="${percentage}"
        aria-label="${this.getMessage('progressLabel')}"
      >
        <div
          id="progress-bar-fill"
          class="h-full bg-highlight transition-all duration-300 ease-in-out"
          style="width: ${percentage}%"
        ></div>
      </div>
      <p
        id="progress-bar-message"
        class="absolute translate-y-0.5 w-full h-max text-left text-small pl-2"
        aria-live="polite"
        aria-atomic="true"
      >${message}</p>
    `;

    return progressBarContainer;
  }

  // Initialize a specific progress bar element
  initializeProgressBarElement(progressBarElement) {
    if (!progressBarElement) return;

    const progressFill = progressBarElement.querySelector('#progress-bar-fill');
    const progressMessage = progressBarElement.querySelector('#progress-bar-message');

    if (progressFill && progressMessage) {
      // Set the current state (element was already created with correct width)
      progressMessage.textContent = this.progressBarState.message;
    }
  }

  // Update the progress bar element
  updateProgressBar(progressData) {
    if (!progressData) return;

    const { percentage, message } = progressData;

    // Update stored state
    this.progressBarState = { percentage, message };

    // Ensure progress bar exists
    this.ensureProgressBarExists();

    // Find the progress bar element
    const progressBarElement = document.getElementById('shipping-progress-bar');
    if (!progressBarElement) return;

    const progressBar = progressBarElement.querySelector('[role="progressbar"]');
    const progressFill = progressBarElement.querySelector('#progress-bar-fill');
    const progressMessage = progressBarElement.querySelector('#progress-bar-message');

    if (progressFill && progressMessage) {
      // Normal smooth transition to new width
      progressFill.style.width = `${percentage}%`;
      progressMessage.textContent = message;
      progressBar?.setAttribute('aria-valuenow', percentage);
    }
  }

  // Subscribe to shipping updates
  subscribe(callback) {
    this.listeners.add(callback);

    // If we already have shipping data, call the callback immediately
    if (this.currentShippingData) {
      callback(this.currentShippingData);
    }

    return () => this.listeners.delete(callback);
  }

  // Resolve a message from theme strings, substituting the formatted amount
  getMessage(key, amount = null, currencyCode = null) {
    const strings = window.theme?.shipping?.strings || {};
    const template = strings[key] || ShippingCalculator.fallbackStrings[key] || '';

    if (amount === null) {
      return template;
    }

    return template.replace('[amount]', this.formatCurrency(amount, currencyCode));
  }

  // Calculate progress bar data based on shipping data
  calculateProgressData(shippingData) {
    let percentage = 0;

    if (!shippingData) {
      return { percentage, message: this.getMessage('empty') };
    }

    const currency = shippingData.currency;
    const threshold = shippingData.freeShippingThreshold || this.getThreshold(currency);

    if (shippingData.status === 'empty') {
      return { percentage: 0, message: this.getMessage('threshold', threshold, currency) };
    }

    if (
      shippingData.status === 'unavailable' ||
      shippingData.status === 'error' ||
      shippingData.status === 'postcode_required'
    ) {
      return { percentage: 0, message: this.getMessage('unavailable') };
    }

    if (shippingData.status !== 'available') {
      return { percentage: 0, message: this.getMessage('empty') };
    }

    if (shippingData.isFreeShipping) {
      return { percentage: 100, message: this.getMessage('qualified') };
    }

    const cartTotal = shippingData.cartTotal || 0;
    const remaining = threshold - cartTotal;

    if (remaining <= 0) {
      // Cart is past the threshold but the carrier still returned a paid rate, so the
      // threshold in theme settings no longer matches the Shopify shipping profile
      return { percentage: 100, message: this.getMessage('unavailable') };
    }

    percentage = Math.min(Math.max((cartTotal / threshold) * 100, 0), 100);

    return {
      percentage: Math.round(percentage),
      message: this.getMessage('remaining', remaining, currency),
    };
  }

  // Notify all subscribers of shipping updates
  notify(shippingData) {
    // Calculate and add progress data
    const progressData = this.calculateProgressData(shippingData);
    const enrichedData = { ...shippingData, progressData };

    // Store the enriched shape so late subscribers receive the same data as live ones
    this.currentShippingData = enrichedData;

    // Update progress bars directly
    this.updateProgressBar(progressData);

    this.listeners.forEach((callback) => callback(enrichedData));
  }

  // Get current country from localization system
  getCurrentCountry() {
    // Try to get from the localization form if it exists
    const localizationForm = document.querySelector('localization-form');
    if (localizationForm) {
      const countryInput = localizationForm.querySelector('input[name="country_code"]');
      if (countryInput && countryInput.value) {
        return countryInput.value;
      }
    }

    // Fallback: try to get from any country localization button
    const countryButton = document.querySelector(
      '[data-country-localization] button[name="country_code"][aria-current="true"]',
    );
    if (countryButton) {
      return countryButton.value;
    }

    // Final fallback: use the server-side localization country (embedded in template)
    const shippingSection = document.getElementById('shipping-cost-section');
    if (shippingSection) {
      const countryCode = shippingSection.dataset.countryCode;
      return countryCode;
    }

    return 'GB'; // Default to GB
  }

  // Calculate shipping for current cart and country
  async calculateShipping(cartData = null, countryCode = null) {
    // Debounce multiple rapid calls
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(async () => {
      await this._performCalculation(cartData, countryCode);
    }, 100); // 100ms debounce
  }

  // Internal method that performs the actual calculation
  async _performCalculation(cartData = null, countryCode = null) {
    // Prevent multiple simultaneous calculations
    if (this.isCalculating) {
      return this.currentShippingData;
    }

    this.isCalculating = true;

    try {
      // Get cart data if not provided
      if (!cartData) {
        const cartResponse = await fetch('/cart.js');
        cartData = await cartResponse.json();
      }

      // Get country if not provided
      if (!countryCode) {
        countryCode = this.getCurrentCountry();
      }

      // Cart totals and shipping rates are both returned in the presentment currency
      const currency = cartData.currency || this.getCurrentCurrency();

      // Create cache key
      const cacheKey = `${countryCode}-${currency}-${cartData.total_price}-${cartData.item_count}`;

      // Check cache first
      if (this.cache.has(cacheKey)) {
        const cachedData = this.cache.get(cacheKey);
        this.notify(cachedData);
        return cachedData;
      }

      // If cart is empty, return appropriate data
      if (cartData.item_count === 0) {
        const emptyCartData = {
          country: countryCode,
          currency,
          cartTotal: 0,
          shippingCost: null,
          isFreeShipping: false,
          freeShippingThreshold: this.getThreshold(currency),
          amountToFreeShipping: null,
          shippingRates: null,
          status: 'empty',
        };
        this.cache.set(cacheKey, emptyCartData);
        this.notify(emptyCartData);
        return emptyCartData;
      }

      // Prepare shipping address
      const shippingAddress = {
        country: countryCode,
        province: '',
        zip: this.defaultPostcodes[countryCode] || '',
        city: '',
        address1: '',
        address2: '',
      };

      // Build query string for shipping rates
      const params = new URLSearchParams({
        'shipping_address[country]': shippingAddress.country,
        'shipping_address[province]': shippingAddress.province,
        'shipping_address[zip]': shippingAddress.zip,
        'shipping_address[city]': shippingAddress.city,
        'shipping_address[address1]': shippingAddress.address1,
        'shipping_address[address2]': shippingAddress.address2,
      });

      // Add cart items to the request
      cartData.items.forEach((item, index) => {
        params.append(`items[${index}][variant_id]`, item.variant_id);
        params.append(`items[${index}][quantity]`, item.quantity);
      });

      const shippingUrl = `/cart/shipping_rates.json?${params.toString()}`;

      // Fetch shipping rates
      const shippingResponse = await fetch(shippingUrl);

      const threshold = this.getThreshold(currency);

      let shippingData = {
        country: countryCode,
        currency,
        cartTotal: cartData.total_price,
        shippingCost: null,
        isFreeShipping: false,
        freeShippingThreshold: threshold,
        amountToFreeShipping: Math.max(threshold - cartData.total_price, 0),
        shippingRates: null,
        status: 'unknown',
      };

      if (shippingResponse.ok) {
        const responseData = await shippingResponse.json();
        const shippingRates = responseData.shipping_rates;

        if (Array.isArray(shippingRates) && shippingRates.length > 0) {
          // Find the cheapest shipping option
          const cheapestRate = shippingRates.reduce((min, rate) =>
            parseFloat(rate.price) < parseFloat(min.price) ? rate : min,
          );

          const shippingCostInMinorUnits = Math.round(parseFloat(cheapestRate.price) * 100);
          const isFreeShipping = shippingCostInMinorUnits === 0;

          shippingData = {
            country: countryCode,
            currency,
            cartTotal: cartData.total_price,
            shippingCost: shippingCostInMinorUnits,
            isFreeShipping,
            freeShippingThreshold: isFreeShipping ? cartData.total_price : threshold,
            amountToFreeShipping: isFreeShipping ? 0 : Math.max(threshold - cartData.total_price, 0),
            shippingRates,
            cheapestRate,
            status: 'available',
          };
        } else {
          shippingData.status = 'unavailable';
        }
      } else {
        const errorText = await shippingResponse.text();
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.zip && errorData.zip.includes('postcode')) {
            shippingData.status = 'postcode_required';
          } else {
            shippingData.status = 'unavailable';
          }
        } catch (e) {
          shippingData.status = 'error';
        }
      }

      // Cache the result
      this.cache.set(cacheKey, shippingData);

      // Notify subscribers
      this.notify(shippingData);

      return shippingData;
    } catch (error) {
      console.error('Shipping calculation error:', error);
      const errorData = {
        country: countryCode || this.getCurrentCountry(),
        currency: cartData?.currency || this.getCurrentCurrency(),
        cartTotal: cartData ? cartData.total_price : 0,
        shippingCost: null,
        isFreeShipping: false,
        freeShippingThreshold: null,
        amountToFreeShipping: null,
        shippingRates: null,
        status: 'error',
      };
      this.notify(errorData);
      return errorData;
    } finally {
      this.isCalculating = false;
    }
  }

  // Get the active presentment currency, which is what cart totals are returned in
  getCurrentCurrency() {
    return window.Shopify?.currency?.active || window.theme?.shipping?.currency || 'GBP';
  }

  // Get the free shipping threshold for the active currency, in that currency's smallest unit
  getThreshold(currencyCode = null) {
    const shipping = window.theme?.shipping;
    const currency = currencyCode || this.getCurrentCurrency();

    return shipping?.thresholds?.[currency] || shipping?.defaultThreshold || 10000;
  }

  // Clear cache (useful when cart changes significantly)
  clearCache() {
    this.cache.clear();
  }

  // Format an amount given in the smallest currency unit, using the active presentment currency
  formatCurrency(amount, currencyCode = null) {
    const currency = currencyCode || this.getCurrentCurrency();
    const locale = window.theme?.shipping?.locale || document.documentElement.lang || 'en-GB';
    const value = amount / 100;

    const options = { style: 'currency', currency };

    // Drop the decimals on whole amounts so messages read "£50" rather than "£50.00"
    if (Number.isInteger(value)) {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }

    try {
      // narrowSymbol keeps USD as "$100" rather than "US$100" when the locale is en-GB
      return new Intl.NumberFormat(locale, { ...options, currencyDisplay: 'narrowSymbol' }).format(value);
    } catch (error) {
      try {
        return new Intl.NumberFormat(locale, options).format(value);
      } catch (fallbackError) {
        // Intl throws on an unrecognised currency code
        return value.toFixed(2);
      }
    }
  }
}

// Create a global instance
if (!window.shippingCalculator) {
  window.shippingCalculator = new ShippingCalculator();
}
